import dispatchToast from 'components/modules/Toast';
import { useMyStore } from 'features/my-store/hooks/useMyStore';
import { allMyStoreType } from 'features/my-store/types/StoreType';
import { getNotifications, readNotifications } from 'features/notification/api';
import { NotificationListItemType } from 'features/notification/types/NotificationType';
import {
  filterByStoreIds,
  filterByStores
} from 'features/notification/utils/filters';
import { hasUnread } from 'features/notification/utils/unread-checker';
import { API_RESPONSE_STATUS, TOAST_ERROR_MESSAGE } from 'libs/constants';
import {
  selectedNotificationStoreIdAtom,
  unreadNotificationAtom
} from 'libs/recoil/atom';
import { activeFeatureCodesState } from 'libs/recoil/selector';
import { useCallback, useEffect, useState } from 'react';
import { useAsyncFn } from 'react-use';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

export const useNotificationList = (
  { firstExecutionWithoutCall }: { firstExecutionWithoutCall: boolean } = {
    firstExecutionWithoutCall: true
  }
) => {
  const setUnreadNotification = useSetRecoilState(unreadNotificationAtom);
  const [currentStoreId, setCurrentStoreId] = useRecoilState(
    selectedNotificationStoreIdAtom
  );
  const [allNotificationList, setAllNotificationList] = useState<
    NotificationListItemType[]
  >([]);
  const [displayedNotificationList, setDisplayedNotificationList] = useState<
    NotificationListItemType[]
  >([]);
  const [unreadIds, setUnreadIds] = useState<number[]>([]);
  const activeFeatureCodes = useRecoilValue(activeFeatureCodesState);
  // すべてのマイ店舗お知らせを表示するため、マイ店舗を取得しておく
  const {
    isFeatureActive: isMyStoreFeatureActive,
    isDone: isMyStoreDone,
    myStores
  } = useMyStore();

  // 一覧取得
  const [{ loading: isNotificationListMutating }, getNotificationList] =
    useAsyncFn(async () => {
      if (!activeFeatureCodes.includes('notice')) return;
      // マイ店舗のAPI読み込みが終わるまでは、未読通知の処理はしない
      if (isMyStoreFeatureActive && !isMyStoreDone) return;

      const res = await getNotifications();
      if (res?.status !== API_RESPONSE_STATUS.SUCCEEDED) {
        dispatchToast({
          id: 'membership-member-api-error',
          toastText: TOAST_ERROR_MESSAGE
        });
        return;
      }

      // 全お知らせ
      setAllNotificationList(res.data.notifications);

      // 未読お知らせのグローバル状態を更新
      setUnreadNotification({
        hasUnread: hasUnread(res.data.notifications, myStores)
      });
    }, [activeFeatureCodes, myStores]);

  // 既読する
  const [{ loading: isReadNotificationMutating }, readNotification] =
    useAsyncFn(
      async (ids: number[]) => {
        if (!ids || ids.length === 0 || !activeFeatureCodes.includes('notice'))
          return;

        const res = await readNotifications(ids);

        if (res?.status === API_RESPONSE_STATUS.SUCCEEDED) {
          // 未読お知らせのグローバル状態を更新するため、再取得する
          getNotificationList();
          return;
        }

        dispatchToast({
          id: 'membership-member-api-error',
          toastText: TOAST_ERROR_MESSAGE
        });
      },
      [activeFeatureCodes, getNotificationList]
    );

  // 店舗で絞り込んだお知らせ
  const notificationsByCurrentStoreId = useCallback(
    (currentStoreId: number) =>
      filterByStoreIds(allNotificationList, [currentStoreId]),
    [allNotificationList]
  );

  // すべてのマイ店舗のお知らせを取得
  const notificationsByMyStores = useCallback(
    () => filterByStores(allNotificationList, myStores),
    [allNotificationList, myStores]
  );

  useEffect(() => {
    if (firstExecutionWithoutCall) getNotificationList();
  }, [getNotificationList, firstExecutionWithoutCall]);

  useEffect(() => {
    // マイ店舗の読み込みが完了するまでは、お知らせリストは表示しない
    if (isMyStoreFeatureActive && !isMyStoreDone) return;

    let list: NotificationListItemType[];
    if (isMyStoreFeatureActive && currentStoreId === allMyStoreType.id) {
      list = notificationsByMyStores();
    } else if (currentStoreId) {
      list = notificationsByCurrentStoreId(currentStoreId);
    } else {
      // 未選択の場合はすべてのお知らせを表示
      list = allNotificationList;
    }
    setDisplayedNotificationList(list);

    // 一覧に表示されている未読お知らせIDs
    const unreadIds = list.filter((item) => !item.read).map((item) => item.id);
    setUnreadIds(unreadIds);
  }, [
    allNotificationList,
    currentStoreId,
    isMyStoreDone,
    isMyStoreFeatureActive,
    notificationsByCurrentStoreId,
    notificationsByMyStores
  ]);

  return {
    displayedNotificationList,
    unreadIds,
    currentStoreId,
    setCurrentStoreId,
    readNotification,
    isReadNotificationMutating,
    getNotificationList,
    isNotificationListMutating
  };
};
